import { OdometerNoTelemetryError, OdometerRequester, type VehicleOdometerAtDate } from '@cobliteam/api-lib/src/dashboard/odometer';
import type { UUID } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import { useQuery } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();

const getOdometerRequester = () => new OdometerRequester({ host: env.apiHost });

const QUERY_KEY = 'VEHICLE_ODOMETER_AT_DATE';

export type OdometerFailureType = 'no_telemetry' | 'error';

// Plausibility floor: the maintenance DatePicker writes `new Date(0)` when the
// day is cleared (MaintenanceOccurenceAt.tsx) — 1970 is not a maintenance date,
// and firing a request for it would pollute both the API and the analytics.
const MIN_PLAUSIBLE_EPOCH_MS = Date.UTC(2000, 0, 1);

// react-query hook returning `{ data, isLoading, error }`. `epochMs` must
// already be settled — debounce it at the call site if the source is a picker,
// so the value that keys the cache is the same one the caller reasons about.
// Returns `null` (not an error) on 404 — the drawer treats no-telemetry as a
// silent state, distinct from a transport failure.
//
// `retry: false` overrides the global `retry: 3` (Dashboard.tsx): the drawer
// shows no error UI for this query, so retrying only multiplied requests (4 per
// 5xx) and delayed the failure event by ~3s. `refetchOnReconnect` is off for
// the same reason — a silent background refetch has nothing to update here.
export const useVehicleOdometerAtDate = (vehicleId: UUID | undefined, epochMs: number | undefined) => {
  const enabled = Boolean(vehicleId) && typeof epochMs === 'number' && Number.isFinite(epochMs) && epochMs >= MIN_PLAUSIBLE_EPOCH_MS;

  return useQuery<VehicleOdometerAtDate | null, Error>({
    queryKey: [QUERY_KEY, vehicleId, epochMs],
    queryFn: async () => {
      try {
        return await getOdometerRequester().getVehicleOdometerAtDate(vehicleId as UUID, epochMs as number);
      } catch (error) {
        if (error instanceof OdometerNoTelemetryError) return null;
        throw error;
      }
    },
    enabled,
    staleTime: 60_000,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export default useVehicleOdometerAtDate;
